import { useRef, useState } from "react";
import type { MessageEvent, PollRecord } from "@veto/domain";
import {
  canDeleteForEveryone,
  canEditMessage,
  decodePollWire,
  decodeSystemEvent,
  isForwardedBody,
} from "@veto/domain";
import {
  DeliveryTicks,
  IconCopy,
  IconDirect,
  IconEdit,
  IconPin,
  IconReply,
  IconShield,
  IconStar,
  IconSticker,
  IconTrash,
} from "@veto/ui";
import { ModalShell } from "./ModalShell";
import { saveStickerFromUrl } from "../stickers/myStickers";
import { FormattedText } from "./FormattedText";
import { GIF_FILE_LABEL_PREFIX, parseStickerRef, STICKER_FILE_LABEL_PREFIX } from "@veto/protocol";
import { getBuiltinStickerByRef } from "./stickerArt";
import { useLongPress } from "../hooks/useLongPress";
import {
  describeTransport,
  securitySheetLines,
  transportTooltip,
} from "../messaging/transportInfo";
import { confirmAction } from "./ConfirmDialog";
import { CONTENT_TYPE_GROUP } from "@veto/protocol";

const QUICK = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

export function MessageBubble({
  msg,
  replyPreview,
  audioUrl,
  onReply,
  onReact,
  onEdit,
  onDeleteMe,
  onDeleteEveryone,
  onForward,
  onStar,
  onPin,
  onOpenViewOnce,
  onVotePoll,
  senderName,
  runStart = true,
  runEnd = true,
  settled = false,
  highlight = "",
  searchHit = false,
  onReport,
}: {
  /** Incoming 1:1 messages: Report & block with this message included. */
  onReport?: () => void;
  /** In-chat search: the term to mark, and whether this is the match being shown. */
  highlight?: string;
  searchHit?: boolean;
  /** Already there when the chat opened: no entrance animation (only new arrivals animate). */
  settled?: boolean;
  msg: MessageEvent;
  /** Group chats: shown on the first message of a run from someone else. */
  senderName?: string;
  /** First / last message of a run from the same sender (spacing and the bubble tail). */
  runStart?: boolean;
  runEnd?: boolean;
  replyPreview?: string;
  audioUrl?: string;
  onReply: () => void;
  onReact: (emoji: string) => void;
  onEdit: () => void;
  onDeleteMe: () => void;
  onDeleteEveryone: () => void;
  onForward?: () => void;
  onStar?: () => void;
  onPin?: () => void;
  onOpenViewOnce?: () => void;
  onVotePoll?: (optionId: string) => void;
}) {
  // Long-press / right-click sheet — hooks must run before any early return.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  // Full-screen photo viewer (phone parity).
  const [viewing, setViewing] = useState(false);
  // Touch: swipe right to reply (phone parity). Mouse users keep the hover toolbar.
  const swipe = useRef<{ x: number; y: number; id: number } | null>(null);
  const [swipeDx, setSwipeDx] = useState(0);
  const lp = useLongPress();
  const transport = describeTransport(msg.transport);

  /** Don't hijack presses that begin on interactive bubble content. */
  function onNestedControl(e: React.PointerEvent | React.MouseEvent) {
    return !!(e.target as HTMLElement).closest(
      "button, a, input, select, textarea, audio, video, label",
    );
  }

  function onBubbleContextMenu(e: React.MouseEvent) {
    if (onNestedControl(e)) return; // native menu stays for links/media
    e.preventDefault();
    lp.cancel();
    setSheetOpen(true);
  }

  if (msg.deletedForMe) return null;
  const system = decodeSystemEvent(msg.body);
  if (system) {
    return <div className="system-event">{system.text}</div>;
  }
  const isVoice = !msg.deletedForEveryone && msg.body.startsWith("🎤");
  const attachLabel =
    !msg.deletedForEveryone && msg.body.startsWith("📎 ") ? msg.body.slice(2) : "";
  const isImageAttach = !!attachLabel && /\.(png|jpe?g|gif|webp)$/i.test(attachLabel);
  const isDocAttach = !!attachLabel && !isImageAttach;
  const stickerRef = !msg.deletedForEveryone ? parseStickerRef(msg.body) : null;
  const stickerFileLabel =
    !msg.deletedForEveryone && !stickerRef && msg.body.startsWith(`${STICKER_FILE_LABEL_PREFIX} `)
      ? msg.body.slice(2).trim()
      : "";
  const gifFileLabel =
    !msg.deletedForEveryone && msg.body.startsWith(`${GIF_FILE_LABEL_PREFIX} `)
      ? msg.body.slice(2).trim()
      : "";
  const forwarded = !msg.deletedForEveryone && isForwardedBody(msg.body);
  const viewOnceBurned = !!msg.viewOnce && !!msg.viewOnceOpenedAt;
  const poll = !msg.deletedForEveryone ? decodePollWire(msg.body) : null;

  return (
    <div
      className={`bubble-row ${msg.direction}${runStart ? " run-start" : ""}${runEnd ? " run-end" : " run-mid"}${settled ? " settled" : ""}${searchHit ? " search-hit" : ""}`}
      onPointerDown={(e) => {
        if (e.pointerType === "touch" && !msg.deletedForEveryone)
          swipe.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
      }}
      onPointerMove={(e) => {
        const s = swipe.current;
        if (!s || s.id !== e.pointerId) return;
        const dx = e.clientX - s.x;
        const dy = e.clientY - s.y;
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
          swipe.current = null;
          setSwipeDx(0);
          return;
        }
        setSwipeDx(dx > 0 ? Math.min(dx, 90) : 0);
      }}
      onPointerUp={() => {
        if (swipeDx > 64) onReply();
        swipe.current = null;
        setSwipeDx(0);
      }}
      onPointerCancel={() => {
        swipe.current = null;
        setSwipeDx(0);
      }}
    >
      {swipeDx > 0 && (
        <span
          className="swipe-reply-hint"
          style={{ opacity: Math.min(1, swipeDx / 64) }}
          aria-hidden
        >
          ↩
        </span>
      )}
      <div
        className={`bubble ${msg.direction}${msg.deletedForEveryone ? " tombstone" : ""}${
          stickerRef || stickerFileLabel ? " bubble-sticker" : ""
        }`}
        style={swipeDx ? { transform: `translateX(${swipeDx}px)` } : undefined}
        onDoubleClick={(e) => {
          // Double-tap / double-click: ❤️ (1:1 chats, like the phone).
          if (
            msg.deletedForEveryone ||
            msg.contentType === CONTENT_TYPE_GROUP ||
            onNestedControl(e)
          )
            return;
          onReact("❤️");
        }}
        onPointerDown={(e) =>
          lp.begin(
            e,
            () => setSheetOpen(true),
            (ev) => !onNestedControl(ev),
          )
        }
        onPointerMove={lp.move}
        onPointerUp={lp.end}
        onPointerCancel={lp.end}
        onPointerLeave={lp.end}
        onContextMenu={onBubbleContextMenu}
      >
        {senderName && (
          <div className="bubble-sender" style={{ color: senderColor(senderName) }}>
            {senderName}
          </div>
        )}
        {replyPreview && !msg.deletedForEveryone && (
          <button type="button" className="reply-quote" onClick={onReply}>
            {replyPreview}
          </button>
        )}
        {forwarded && <div className="forward-label">Forwarded</div>}
        {(msg.starred || msg.pinned) && !msg.deletedForEveryone && (
          <div className="forward-label">
            {msg.pinned ? "Pinned" : ""}
            {msg.pinned && msg.starred ? " · " : ""}
            {msg.starred ? "Starred" : ""}
          </div>
        )}
        <div className="bubble-body">
          {msg.deletedForEveryone ? (
            <em className="muted">This message was deleted</em>
          ) : isVoice ? (
            <div className="voice-bubble">
              <span>{msg.body}</span>
              {audioUrl ? (
                <audio controls preload="metadata" src={audioUrl} className="voice-player" />
              ) : (
                <span className="muted text-sm">
                  Encrypted audio (playback when chunks available)
                </span>
              )}
            </div>
          ) : msg.viewOnce ? (
            <div className="attach-card view-once-card">
              {viewOnceBurned ? (
                <div className="view-once-burned" role="status">
                  <span className="attach-kind">View once</span>
                  <strong>Opened and removed</strong>
                  <span className="muted text-sm">
                    Media burned on this device after open. Recipients may still retain copies.
                  </span>
                </div>
              ) : (
                <>
                  <span className="attach-kind">View once</span>
                  <FormattedText
                    text={msg.body}
                    mentions={msg.contentType === CONTENT_TYPE_GROUP}
                    highlight={highlight}
                  />
                  <span className="muted text-sm view-once-hint">
                    Opens once, then removed from this device.
                  </span>
                  {audioUrl ? (
                    <button
                      type="button"
                      className="btn small"
                      data-testid="view-once-ready"
                      onClick={onOpenViewOnce}
                      title="Open once — media is removed afterward on this device"
                    >
                      Open once
                    </button>
                  ) : (
                    <span className="muted text-sm attach-pending" role="status">
                      Encrypted view-once — media bytes not ready yet. Retry when connected.
                    </span>
                  )}
                </>
              )}
            </div>
          ) : stickerRef ? (
            <StickerRefView refKey={`${stickerRef.pack}/${stickerRef.id}`} />
          ) : stickerFileLabel ? (
            audioUrl ? (
              <div className="sticker-card">
                <img src={audioUrl} alt="" className="sticker-render" />
              </div>
            ) : (
              <div className="attach-card attach-pending-card">
                <span className="attach-kind">Sticker</span>
                <span className="muted text-sm attach-pending" role="status">
                  Encrypted sticker — bytes pending. Stay online to finish download.
                </span>
              </div>
            )
          ) : gifFileLabel ? (
            audioUrl ? (
              <div className="gif-card">
                <img src={audioUrl} alt={gifFileLabel} className="gif-render" />
                <span className="muted text-sm attach-label">{gifFileLabel}</span>
              </div>
            ) : (
              <div className="attach-card attach-pending-card">
                <span className="attach-kind">GIF</span>
                <span className="muted text-sm attach-pending" role="status">
                  Encrypted GIF — bytes pending. Stay online to finish download.
                </span>
              </div>
            )
          ) : isImageAttach && audioUrl ? (
            <div className="attach-card">
              <button
                type="button"
                className="attach-preview-btn"
                onClick={() => setViewing(true)}
                aria-label={`Open ${attachLabel}`}
              >
                <img src={audioUrl} alt={attachLabel} className="attach-preview" />
              </button>
              <span className="muted text-sm attach-label">{attachLabel}</span>
            </div>
          ) : isImageAttach ? (
            <div className="attach-card attach-pending-card">
              <span className="attach-kind">Image</span>
              <FormattedText
                text={msg.body}
                mentions={msg.contentType === CONTENT_TYPE_GROUP}
                highlight={highlight}
              />
              <span className="muted text-sm attach-pending" role="status">
                Preview unavailable — encrypted bytes pending. Stay online to finish download.
              </span>
            </div>
          ) : isDocAttach ? (
            <div className="attach-card">
              <span className="attach-kind">Document</span>
              <FormattedText
                text={msg.body}
                mentions={msg.contentType === CONTENT_TYPE_GROUP}
                highlight={highlight}
              />
              {audioUrl ? (
                <a className="attach-download" href={audioUrl} download={attachLabel}>
                  Download {attachLabel}
                </a>
              ) : (
                <span className="muted text-sm attach-pending" role="status">
                  Encrypted document — download when bytes arrive. Attachment send failures show as
                  a toast.
                </span>
              )}
            </div>
          ) : poll ? (
            <PollCard poll={poll} onVotePoll={onVotePoll} />
          ) : (
            <FormattedText
              text={msg.body}
              mentions={msg.contentType === CONTENT_TYPE_GROUP}
              highlight={highlight}
            />
          )}
          {msg.editedAt && !msg.deletedForEveryone ? (
            <span className="edited-tag"> edited</span>
          ) : null}
        </div>
        {!!msg.reactions?.length && !msg.deletedForEveryone && (
          <div className="reaction-bar">
            {summarize(msg.reactions).map(([emoji, n]) => (
              <button
                key={emoji}
                type="button"
                className="reaction-chip"
                onClick={() => onReact(emoji)}
              >
                {emoji} {n > 1 ? n : ""}
              </button>
            ))}
          </div>
        )}
        <div className="meta">
          {msg.expiresAt && !msg.deletedForEveryone && (
            <span
              className="timer-icon"
              title="Disappearing message"
              aria-label="Disappearing message"
            >
              ⏱
            </span>
          )}
          <span>
            {new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
          {msg.transport && (
            <span
              className={`transport-badge tone-${transport.tone}`}
              role="status"
              aria-label={`Transport: ${transport.label}`}
              title={transportTooltip(msg)}
              onClick={() => setSecurityOpen(true)}
            >
              {transport.label}
            </span>
          )}
          {msg.direction === "out" && <DeliveryTicks state={msg.status} />}
        </div>
        {!msg.deletedForEveryone && (
          <div className="msg-actions" role="toolbar" aria-label="Message actions">
            <button type="button" onClick={onReply}>
              Reply
            </button>
            {onForward && (
              <button type="button" onClick={onForward}>
                Forward
              </button>
            )}
            {onStar && (
              <button type="button" onClick={onStar} aria-pressed={!!msg.starred}>
                {msg.starred ? "Unstar" : "Star"}
              </button>
            )}
            {onPin && (
              <button type="button" onClick={onPin} aria-pressed={!!msg.pinned}>
                {msg.pinned ? "Unpin" : "Pin"}
              </button>
            )}
            <span className="msg-actions-sep" aria-hidden />
            {QUICK.map((e) => (
              <button
                key={e}
                type="button"
                className="react"
                onClick={() => onReact(e)}
                aria-label={`React ${e}`}
              >
                {e}
              </button>
            ))}
            <span className="msg-actions-sep" aria-hidden />
            {canEditMessage(msg) && (
              <button type="button" onClick={onEdit}>
                Edit
              </button>
            )}
            <button
              type="button"
              className="msg-action-danger"
              onClick={() => {
                void confirmAction({
                  title: "Delete for you?",
                  body: "Removed from this device only.",
                  action: "Delete",
                }).then((ok) => ok && onDeleteMe());
              }}
            >
              Delete
            </button>
            {canDeleteForEveryone(msg) && (
              <button
                type="button"
                className="msg-action-danger"
                onClick={() => {
                  void confirmAction({
                    title: "Delete for everyone?",
                    body: 'Everyone sees "This message was deleted". They may already have read or copied it.',
                    action: "Delete for everyone",
                  }).then((ok) => ok && onDeleteEveryone());
                }}
              >
                Delete all
              </button>
            )}
          </div>
        )}
      </div>
      {viewing && audioUrl && (
        <ModalShell
          title={attachLabel || "Photo"}
          ariaLabel="Photo"
          onClose={() => setViewing(false)}
          size="lg"
          className="lightbox-modal"
        >
          <img src={audioUrl} alt={attachLabel} className="lightbox-img" />
          <div className="actions-row">
            <a className="btn secondary small" href={audioUrl} download={attachLabel || "photo"}>
              Download
            </a>
          </div>
        </ModalShell>
      )}
      {sheetOpen && !msg.deletedForEveryone && (
        <MessageActionSheet
          msg={msg}
          onReport={
            onReport && msg.direction === "in"
              ? () => {
                  setSheetOpen(false);
                  onReport();
                }
              : undefined
          }
          onClose={() => setSheetOpen(false)}
          onSecurity={() => {
            setSheetOpen(false);
            setSecurityOpen(true);
          }}
          onReply={() => {
            setSheetOpen(false);
            onReply();
          }}
          onReact={(emoji) => {
            setSheetOpen(false);
            onReact(emoji);
          }}
          onEdit={
            canEditMessage(msg)
              ? () => {
                  setSheetOpen(false);
                  onEdit();
                }
              : undefined
          }
          onDeleteMe={() => {
            setSheetOpen(false);
            void confirmAction({
              title: "Delete for you?",
              body: "Removed from this device only.",
              action: "Delete",
            }).then((ok) => ok && onDeleteMe());
          }}
          onDeleteEveryone={
            canDeleteForEveryone(msg)
              ? () => {
                  setSheetOpen(false);
                  void confirmAction({
                    title: "Delete for everyone?",
                    body: 'Everyone sees "This message was deleted". They may already have read or copied it.',
                    action: "Delete for everyone",
                  }).then((ok) => ok && onDeleteEveryone());
                }
              : undefined
          }
          onForward={
            onForward
              ? () => {
                  setSheetOpen(false);
                  onForward();
                }
              : undefined
          }
          onStar={
            onStar
              ? () => {
                  setSheetOpen(false);
                  onStar();
                }
              : undefined
          }
          onPin={
            onPin
              ? () => {
                  setSheetOpen(false);
                  onPin();
                }
              : undefined
          }
          onSaveSticker={
            stickerFileLabel && audioUrl
              ? () => {
                  setSheetOpen(false);
                  void saveStickerFromUrl(audioUrl);
                }
              : undefined
          }
        />
      )}
      {securityOpen && <MessageSecuritySheet msg={msg} onClose={() => setSecurityOpen(false)} />}
    </div>
  );
}

/** The phone's words for delivery states (Message info). */
const STATUS_WORDS: Partial<Record<MessageEvent["status"], [string, string]>> = {
  queued: [
    "Waiting to send",
    "Stored encrypted on this device; it goes out as soon as a relay is reachable.",
  ],
  sent: ["Sent", "One relay has stored it for delivery."],
  backed_up: [
    "Sent (backed up)",
    "Two relays have stored it, so it survives one relay going down.",
  ],
  delivered: ["Delivered", "Relays confirmed it."],
  read: ["Read", "They opened it."],
  failed: ["Not sent", "Sending failed. It will be retried."],
};

function MessageSecuritySheet({ msg, onClose }: { msg: MessageEvent; onClose: () => void }) {
  const transport = describeTransport(msg.transport);
  const lines = securitySheetLines(msg);
  return (
    <ModalShell
      title="Message info"
      ariaLabel="Message info"
      onClose={onClose}
      size="sheet"
      className="chat-menu-sheet"
    >
      <dl className="info-rows">
        <dt>{msg.direction === "out" ? "Sent" : "Received"}</dt>
        <dd>{new Date(msg.createdAt).toLocaleString()}</dd>
        {msg.editedAt ? (
          <>
            <dt>Edited</dt>
            <dd>{new Date(msg.editedAt).toLocaleString()}</dd>
          </>
        ) : null}
        {msg.direction === "out" && (
          <>
            <dt>Status</dt>
            <dd>
              {STATUS_WORDS[msg.status]?.[0] ?? "Sent"}
              <span className="muted text-sm info-detail">
                {STATUS_WORDS[msg.status]?.[1] ?? ""}
              </span>
            </dd>
          </>
        )}
        {msg.expiresAt ? (
          <>
            <dt>Disappears</dt>
            <dd>{new Date(msg.expiresAt).toLocaleString()}</dd>
          </>
        ) : null}
        <dt>Encryption</dt>
        <dd>End-to-end encrypted. Relays only carried a sealed envelope.</dd>
      </dl>
      <div className={`security-card tone-${transport.tone}`} role="status">
        <div className="security-tone-row">
          <span className={`transport-badge tone-${transport.tone}`}>
            {transport.label || "Encrypted"}
          </span>
          <span className="muted text-sm">
            {msg.direction === "out" ? "Outgoing message" : "Incoming message"}
          </span>
        </div>
        <ul className="security-lines">
          {lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
        <p className="muted text-sm">
          Relays and fallback transports only ever handle opaque ciphertext. The transport tier only
          changes who can observe metadata (sender prefix, timing, size).
        </p>
      </div>
    </ModalShell>
  );
}

function MessageActionSheet({
  msg,
  onClose,
  onReply,
  onSecurity,
  onReact,
  onEdit,
  onDeleteMe,
  onDeleteEveryone,
  onForward,
  onStar,
  onPin,
  onReport,
  onSaveSticker,
}: {
  msg: MessageEvent;
  onReport?: () => void;
  onClose: () => void;
  onReply: () => void;
  onSecurity: () => void;
  onReact: (emoji: string) => void;
  onEdit?: () => void;
  onDeleteMe: () => void;
  onDeleteEveryone?: () => void;
  onForward?: () => void;
  onStar?: () => void;
  onPin?: () => void;
  /** Photo sticker with decrypted bytes on this device → keep it in My stickers. */
  onSaveSticker?: () => void;
}) {
  const canCopy =
    typeof navigator !== "undefined" && !!navigator.clipboard?.writeText && !!msg.body;
  const preview = msg.body.replace(/\s+/g, " ").trim().slice(0, 60);

  return (
    <ModalShell
      title={preview || "Message"}
      ariaLabel="Message actions"
      onClose={onClose}
      size="sheet"
      className="chat-menu-sheet"
    >
      <div className="msg-reactions-row" role="group" aria-label="React to message">
        {QUICK.map((e) => (
          <button
            key={e}
            type="button"
            className="msg-react-big"
            onClick={() => onReact(e)}
            aria-label={`React ${e}`}
          >
            {e}
          </button>
        ))}
      </div>
      <div className="chat-menu-list">
        <button type="button" className="chat-menu-row" onClick={onReply}>
          <IconReply size={18} />
          <span>Reply</span>
        </button>
        {onForward && (
          <button type="button" className="chat-menu-row" onClick={onForward}>
            <IconDirect size={18} />
            <span>Forward</span>
          </button>
        )}
        {onStar && (
          <button
            type="button"
            className="chat-menu-row"
            onClick={onStar}
            aria-pressed={!!msg.starred}
          >
            <IconStar size={18} />
            <span>{msg.starred ? "Unstar" : "Star"}</span>
          </button>
        )}
        {onPin && (
          <button
            type="button"
            className="chat-menu-row"
            onClick={onPin}
            aria-pressed={!!msg.pinned}
          >
            <IconPin size={18} />
            <span>{msg.pinned ? "Unpin" : "Pin"}</span>
          </button>
        )}
        {onSaveSticker && (
          <button type="button" className="chat-menu-row" onClick={onSaveSticker}>
            <IconSticker size={18} />
            <span>Save to My stickers</span>
          </button>
        )}
        {canCopy && (
          <button
            type="button"
            className="chat-menu-row"
            onClick={() => {
              void navigator.clipboard.writeText(msg.body).then(onClose, onClose);
            }}
          >
            <IconCopy size={18} />
            <span>Copy text</span>
          </button>
        )}
        <button type="button" className="chat-menu-row security" onClick={onSecurity}>
          <IconShield size={18} />
          <span>Message info</span>
        </button>
        {onEdit && (
          <button type="button" className="chat-menu-row" onClick={onEdit}>
            <IconEdit size={18} />
            <span>Edit</span>
          </button>
        )}
        <button type="button" className="chat-menu-row danger" onClick={onDeleteMe}>
          <IconTrash size={18} />
          <span>Delete for me</span>
        </button>
        {onDeleteEveryone && (
          <button type="button" className="chat-menu-row danger" onClick={onDeleteEveryone}>
            <IconTrash size={18} />
            <span>Delete for everyone</span>
          </button>
        )}
        {onReport && (
          <button type="button" className="chat-menu-row danger" onClick={onReport}>
            <IconShield size={18} />
            <span>Report</span>
          </button>
        )}
      </div>
    </ModalShell>
  );
}

/** Renders a built-in sticker reference from the local bundled pack. */
function StickerRefView({ refKey }: { refKey: string }) {
  const sticker = getBuiltinStickerByRef(refKey);
  if (!sticker) {
    return (
      <div className="sticker-card sticker-unknown" role="status">
        <span className="sticker-unknown-emoji" aria-hidden>
          🏷️
        </span>
        <span className="muted text-sm">Sticker from a newer pack — update Veto to view it.</span>
      </div>
    );
  }
  return (
    <div className="sticker-card">
      <img
        src={sticker.uri}
        alt=""
        className={`sticker-render${sticker.animated ? " animated" : ""}`}
      />
    </div>
  );
}

function summarize(reactions: NonNullable<MessageEvent["reactions"]>): [string, number][] {
  const map = new Map<string, number>();
  for (const r of reactions) map.set(r.emoji, (map.get(r.emoji) ?? 0) + 1);
  return [...map.entries()];
}

function PollCard({
  poll,
  onVotePoll,
}: {
  poll: PollRecord;
  onVotePoll?: (optionId: string) => void;
}) {
  const totalVotes = poll.options.reduce((n, o) => n + o.voterDevices.length, 0);
  const closed = !!poll.closedAt;
  return (
    <div
      className={`poll-card${closed ? " closed" : ""}`}
      role="group"
      aria-label={`Poll: ${poll.question}`}
    >
      <strong>{poll.question}</strong>
      {totalVotes === 0 && !closed && (
        <span className="muted text-sm poll-empty" role="status">
          No votes yet — tap an option to vote.
        </span>
      )}
      {poll.options.map((o) => {
        const count = o.voterDevices.length;
        const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
        const leading =
          totalVotes > 0 &&
          count > 0 &&
          count === Math.max(...poll.options.map((x) => x.voterDevices.length));
        return (
          <button
            key={o.id}
            type="button"
            className={`poll-opt${count > 0 ? " has-votes" : ""}${leading ? " leading" : ""}`}
            disabled={closed || !onVotePoll}
            onClick={() => onVotePoll?.(o.id)}
            aria-label={`${o.text}, ${count} vote${count === 1 ? "" : "s"}${closed ? ", poll closed" : ""}`}
          >
            <span className="poll-opt-fill" style={{ width: `${pct}%` }} aria-hidden />
            <span className="poll-opt-row">
              <span>{o.text}</span>
              <span className="muted">{count}</span>
            </span>
          </button>
        );
      })}
      <span className="muted text-sm poll-footer">
        {closed
          ? `Closed · ${totalVotes} vote${totalVotes === 1 ? "" : "s"}`
          : totalVotes > 0
            ? `${totalVotes} vote${totalVotes === 1 ? "" : "s"}`
            : "Open for votes"}
      </span>
    </div>
  );
}

/** A stable, readable colour per sender name (same idea as the phone's sender labels). */
const SENDER_COLORS = [
  "#4da3ff",
  "#f2557a",
  "#1fb59a",
  "#f2a93b",
  "#8e5cf7",
  "#3dc5d6",
  "#e66a3c",
  "#5ab552",
];
function senderColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return SENDER_COLORS[h % SENDER_COLORS.length]!;
}
